import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput, { findCountry } from "@/components/PhoneInput";
import { toast } from "sonner";
import { Loader2, ChevronLeft, KeyRound } from "lucide-react";
import { openGoogleRedirect } from "@/lib/googleAuth";
import api, { errMsg } from "@/lib/api";

/**
 * Login screen — single view.
 *  - "Sign in with" row of icon buttons: Google (full-page redirect, account chooser)
 *    + Mobile (opens OTP flow inline).
 *  - Email + password shown as a secondary collapsible for existing accounts.
 *  - "New user? Sign up" link points to the simplified sign-up.
 */
export default function Login() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("choose"); // choose | mobile | email
  const [busy, setBusy] = useState(false);

  // Mobile OTP
  const [dial, setDial] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState(1); // 1 = phone, 2 = code
  const [otpHint, setOtpHint] = useState("");

  // Email fallback
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const goGoogle = () => openGoogleRedirect({ redirectTo: "/app" });

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    const country = findCountry(dial);
    if (phone.length !== country.max) return toast.error(`Enter your ${country.max}-digit mobile number`);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone_number: `${dial}${phone}` });
      setOtpStep(2);
      setOtpHint(data.hint || "");
      toast.success(data.hint ? "OTP sent (MOCK)" : "OTP sent to your phone");
    } catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", {
        phone_number: `${dial}${phone}`, code: otp, terms_accepted: true,
      });
      localStorage.setItem("ns_token", data.token);
      setUser(data.user);
      toast.success(data.is_new ? "Welcome to NextScene!" : "Welcome back!");
      nav(data.needs_onboarding ? "/app/onboarding" : "/app");
    } catch (err) { toast.error(errMsg(err, "Invalid OTP")); }
    finally { setBusy(false); }
  };

  const doEmailLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/app");
    } catch (err) { toast.error(errMsg(err, "Login failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0605] flex items-center justify-center px-6 grain">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size="lg" className="justify-center inline-flex"/>
          <p className="mt-3 text-zinc-400 text-sm">Welcome back. The stage awaits.</p>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-6">
          {mode === "choose" && (
            <div data-testid="signin-chooser" className="space-y-4">
              <p className="text-center text-xs uppercase tracking-[0.2em] text-zinc-400">Sign in with</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="signin-google"
                  onClick={goGoogle}
                  className="h-14 rounded-2xl border border-zinc-700 bg-white text-black font-semibold flex items-center justify-center gap-2 hover:bg-zinc-100 transition-colors"
                >
                  <GoogleIcon/> Google
                </button>
                <button
                  data-testid="signin-mobile"
                  onClick={() => setMode("mobile")}
                  className="h-14 rounded-2xl border border-zinc-700 bg-zinc-900 text-white font-semibold flex items-center justify-center gap-2 hover:border-[#DC2626]/50 hover:bg-zinc-800 transition-colors"
                >
                  <MobileIcon/> Mobile
                </button>
              </div>
              <div className="pt-2">
                <button
                  data-testid="signin-email-toggle"
                  onClick={() => setMode("email")}
                  className="w-full h-11 rounded-full border border-zinc-800 bg-transparent text-zinc-300 text-sm hover:border-[#DC2626]/40 hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                >
                  <KeyRound size={13}/> Use email + password
                </button>
              </div>
            </div>
          )}

          {mode === "mobile" && (
            <div data-testid="signin-mobile-panel" className="space-y-4">
              <button data-testid="mobile-back" onClick={() => { setMode("choose"); setOtpStep(1); setOtp(""); }} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1"><ChevronLeft size={12}/> Back</button>
              {otpStep === 1 ? (
                <form onSubmit={sendOtp} className="space-y-4" data-testid="mobile-form-step1">
                  <div>
                    <Label className="text-zinc-300 text-xs">Mobile number</Label>
                    <PhoneInput
                      dial={dial}
                      onDialChange={(v) => { setDial(v); setPhone(""); }}
                      number={phone}
                      onNumberChange={setPhone}
                      testIdPrefix="login-phone"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">You'll receive a one-time 6-digit code.</p>
                  </div>
                  <Button data-testid="send-otp-btn" disabled={busy || phone.length !== findCountry(dial).max} type="submit" className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
                    {busy ? <Loader2 className="animate-spin" size={16}/> : "Send OTP"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={verifyOtp} className="space-y-4" data-testid="mobile-form-step2">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-3 py-2 flex items-center justify-between">
                    <span className="text-sm text-zinc-300">Code sent to {dial} {phone}</span>
                    <button type="button" data-testid="change-phone-btn" onClick={() => { setOtpStep(1); setOtp(""); }} className="text-xs text-[#DC2626] hover:underline inline-flex items-center gap-0.5"><ChevronLeft size={12}/> Change</button>
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs">6-digit OTP</Label>
                    <Input data-testid="login-otp" inputMode="numeric" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626] tracking-[0.35em] text-center text-lg font-mono"/>
                    {otpHint && <p className="text-[11px] text-amber-300 mt-1">MOCK: use {otpHint.replace(/^MOCK OTP:\s*/, "")}</p>}
                  </div>
                  <Button data-testid="verify-otp-btn" disabled={busy || otp.length !== 6} type="submit" className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
                    {busy ? <Loader2 className="animate-spin" size={16}/> : "Verify & continue"}
                  </Button>
                  <button type="button" data-testid="resend-otp-btn" onClick={sendOtp} disabled={busy} className="w-full text-xs text-zinc-400 hover:text-white">Didn't get it? Resend</button>
                </form>
              )}
            </div>
          )}

          {mode === "email" && (
            <form onSubmit={doEmailLogin} className="space-y-4" data-testid="signin-email-panel">
              <button type="button" data-testid="email-back" onClick={() => setMode("choose")} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1"><ChevronLeft size={12}/> Back</button>
              <div>
                <Label className="text-zinc-300 text-xs">Email</Label>
                <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626]"/>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Password</Label>
                <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626]"/>
              </div>
              <Button data-testid="login-submit" disabled={busy} type="submit" className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
                {busy && <Loader2 className="animate-spin mr-2" size={16}/>} Log in
              </Button>
            </form>
          )}
        </div>

        <p className="text-center mt-6 text-sm text-zinc-400">
          New user? <Link data-testid="go-signup" to="/signup" className="text-[#DC2626] hover:underline font-medium">Sign Up</Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function MobileIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
      <line x1="12" y1="18" x2="12" y2="18"/>
    </svg>
  );
}
